import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, ChevronDown, ExternalLink, Package, Building2, Phone } from 'lucide-react';
import { useAuthContext, useCartTotals, useCartDrawer } from '@flote/app-core';
import { useModal, ModalType } from '../../lib/modal';
import { cn } from '@flote/ui';
import { UserDropdown } from './UserDropdown';

interface NavItem {
  label: string;
  href: string;
  external?: boolean;
}

const primaryNavItems: NavItem[] = [
  { label: 'Products', href: '/products' },
  { label: 'Brands', href: '/brands' },
];

export function Header() {
  const location = useLocation();
  const { isAuthenticated, logout, isLoading: authLoading, customer } = useAuthContext();
  const { totalItems } = useCartTotals();
  const { open: openCart } = useCartDrawer();
  const { openModal } = useModal();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isRoleMenuOpen, setIsRoleMenuOpen] = useState(false);
  const roleMenuRef = useRef<HTMLDivElement>(null);

  // Close role menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (roleMenuRef.current && !roleMenuRef.current.contains(event.target as Node)) {
        setIsRoleMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSignIn = useCallback(() => {
    openModal(ModalType.AUTH_MODAL, {});
    setIsMobileMenuOpen(false);
  }, [openModal]);

  const handleSignOut = useCallback(async () => {
    await logout();
    setIsMobileMenuOpen(false);
  }, [logout]);

  const handleCartClick = useCallback(() => {
    if (!isAuthenticated) {
      openModal(ModalType.AUTH_MODAL, {});
      return;
    }
    openCart();
    setIsMobileMenuOpen(false);
  }, [isAuthenticated, openModal, openCart]);

  const handleSupplierLogin = useCallback(() => {
    window.open('https://randedistributing.b2bwave.com/suppliers/sign_in', '_blank', 'noopener,noreferrer');
    setIsRoleMenuOpen(false);
  }, []);

  const handleSalesRepLogin = useCallback(() => {
    window.open('https://randedistributing.b2bwave.com/sales_reps/sign_in', '_blank', 'noopener,noreferrer');
    setIsRoleMenuOpen(false);
  }, []);

  const handleAdminLogin = useCallback(() => {
    window.open('https://randedistributing.b2bwave.com/users/sign_in', '_blank', 'noopener,noreferrer');
    setIsRoleMenuOpen(false);
  }, []);

  const cartItemCount = useMemo(() => totalItems, [totalItems]);

  const isActiveRoute = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header className="bg-white/95 border-b border-gray-200 sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo + Primary Nav */}
          <div className="flex items-center gap-8">
            <Link to="/brands" className="flex-shrink-0">
              <img src="/Flote_Word_Red.png" alt="Flote" className="h-8" />
            </Link>

            {/* Desktop Primary Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {primaryNavItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActiveRoute(item.href)
                      ? 'bg-primary/10 text-primary'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-2">
            {/* Other Logins - subtle placement */}
            <div className="relative" ref={roleMenuRef}>
              <button
                onClick={() => setIsRoleMenuOpen(!isRoleMenuOpen)}
                className={cn(
                  'flex items-center gap-1 px-3 py-2 rounded-md text-sm transition-colors',
                  isRoleMenuOpen
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                )}
              >
                <span>Portal Logins</span>
                <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', isRoleMenuOpen && 'rotate-180')} />
              </button>

              {isRoleMenuOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    onClick={handleSupplierLogin}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Supplier Portal
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                  <button
                    onClick={handleSalesRepLogin}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Sales Rep Portal
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                  <button
                    onClick={handleAdminLogin}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Admin Portal
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="h-6 w-px bg-gray-200 mx-1" />

            {isAuthenticated ? (
              <>
                <button
                  onClick={handleCartClick}
                  className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                  aria-label={`Shopping cart with ${cartItemCount} items`}
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-xs flex items-center justify-center font-medium">
                      {cartItemCount > 99 ? '99+' : cartItemCount}
                    </span>
                  )}
                </button>
                <UserDropdown />
              </>
            ) : (
              <button
                onClick={handleSignIn}
                disabled={authLoading}
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile: Cart + Menu */}
          <div className="flex md:hidden items-center gap-2">
            {isAuthenticated && (
              <button
                onClick={handleCartClick}
                className="relative p-2 text-gray-600"
                aria-label={`Shopping cart with ${cartItemCount} items`}
              >
                <ShoppingCart className="w-5 h-5" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-xs flex items-center justify-center font-medium">
                    {cartItemCount > 99 ? '99+' : cartItemCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-md"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          {/* Auth Actions - User info first */}
          <div className="px-4 py-3">
            {isAuthenticated && customer ? (
              <div className="space-y-3">
                {/* Customer Info */}
                <div className="px-3 py-2 bg-gray-50 rounded-md">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {customer.email}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-600">
                    <Building2 className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{customer.companyName}</span>
                  </div>
                  {customer.name && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {customer.name}
                    </p>
                  )}
                  {customer.phone && (
                    <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-500">
                      <Phone className="w-3 h-3 flex-shrink-0" />
                      <span>{customer.phone}</span>
                    </div>
                  )}
                </div>

                {/* Order History Link */}
                <Link
                  to="/orders"
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <Package className="w-4 h-4" />
                  <span>Order History</span>
                </Link>

                {/* Sign Out */}
                <button
                  onClick={handleSignOut}
                  disabled={authLoading}
                  className="w-full px-3 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <button
                onClick={handleSignIn}
                disabled={authLoading}
                className="w-full px-4 py-2.5 rounded-md text-sm font-medium text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Primary Navigation */}
          <div className="border-t border-gray-200 px-4 py-3 space-y-1">
            {primaryNavItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'block px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                  isActiveRoute(item.href)
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Other Logins */}
          <div className="border-t border-gray-200 px-4 py-3">
            <p className="px-3 text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Portal Logins</p>
            <button
              onClick={handleSupplierLogin}
              className="flex items-center justify-between w-full px-3 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
            >
              Supplier Portal
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </button>
            <button
              onClick={handleAdminLogin}
              className="flex items-center justify-between w-full px-3 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
            >
              Admin Portal
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}